import { useState } from "react";
import { Card } from "../ui/Card";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import {
  Search,
  Filter,
  MoreHorizontal,
  Edit2,
  Trash2,
  Eye,
  CheckCircle2,
  XCircle,
  Copy,
  BarChart3,
  Ticket,
} from "lucide-react";
import { TableActionMenu } from "../ui/TableActionMenu";
import { useAuth } from "../../contexts/AuthContext";

export interface Coupon {
  _id: string;
  code: string;
  description?: string;
  discountType: "percentage" | "fixed";
  discountValue: number;
  maxDiscountAmount?: number;
  minPurchaseAmount: number;
  applicableScope: "all" | "specific" | "category";
  applicableCourses: string[];
  usageLimit: number | null;
  usedCount: number;
  usageLimitPerUser: number;
  startDate: string;
  expiresAt: string;
  isActive: boolean;
  couponType: string;
  analytics: {
    totalRevenue: number;
    totalDiscount: number;
    conversionRate: number;
  };
  createdAt: string;
}

interface CouponTableProps {
  coupons: Coupon[];
  description?: string;
  isLoading?: boolean;
  totalCoupons: number;
  currentPage: number;
  pageSize: number;
  searchTerm: string;
  statusFilter: "all" | "active" | "inactive";
  onSearchChange: (value: string) => void;
  onStatusFilterChange: (status: "all" | "active" | "inactive") => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onAddClick?: () => void;
  onEditClick: (coupon: Coupon) => void;
  onDeleteClick: (coupon: Coupon) => void;
  onViewClick: (coupon: Coupon) => void;
  onToggleStatus?: (coupon: Coupon) => void;
}

export function CouponTable({
  coupons,
  description = "Manage discount coupons and promotions.",
  isLoading,
  totalCoupons,
  currentPage,
  pageSize,
  searchTerm,
  statusFilter,
  onSearchChange,
  onStatusFilterChange,
  onPageChange,
  onPageSizeChange,
  onAddClick,
  onEditClick,
  onDeleteClick,
  onViewClick,
  onToggleStatus,
}: CouponTableProps) {
  const { hasPermission } = useAuth();

  const canCreate = hasPermission('coupons:create');
  const canUpdate = hasPermission('coupons:update');
  const canDelete = hasPermission('coupons:delete');
  const canToggle = hasPermission('coupons:toggle-status');
  const canViewUsage = hasPermission('coupons:view-usage');

  const [activeMenu, setActiveMenu] = useState<{
    id: string;
    position: { top: number; right: number };
  } | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCoupons / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCoupons);

  const getStatusBadge = (coupon: Coupon) => {
    if (isExpired(coupon.expiresAt)) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold bg-[#F0F0F0] text-[#666666]">
          Expired
        </span>
      );
    }
    if (coupon.isActive) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold bg-[#E8F5EE] text-[#1B8754]">
          <CheckCircle2 size={12} className="mr-1" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold bg-[#FFF8E6] text-[#FFB020]">
        <XCircle size={12} className="mr-1" />
        Inactive
      </span>
    );
  };

  const getDiscountDisplay = (coupon: Coupon) => {
    if (coupon.discountType === "percentage") {
      return `${coupon.discountValue}%`;
    }
    return `$${coupon.discountValue}`;
  };

  const getUsageDisplay = (coupon: Coupon) => {
    if (coupon.usageLimit === null) {
      return `${coupon.usedCount} / ∞`;
    }
    return `${coupon.usedCount} / ${coupon.usageLimit}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">
          Coupons
        </h2>
        <p className="text-gray-500">{description}</p>
      </div>

      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        {/* Search input */}
        <div className="w-full sm:w-[408px]">
          <Input
            placeholder="Search by code or description"
            icon={<Search size={20} />}
            className="bg-white"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {/* Filter buttons and primary action */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full xl:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Button
              variant="outline"
              size="sm"
              className="w-full h-[44px] sm:h-[44px] justify-center"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <Filter size={16} /> Filter
            </Button>
            {isFilterOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsFilterOpen(false)}
                />
                <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[120px] animate-in fade-in zoom-in duration-150">
                  {["all", "active", "inactive"].map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        onStatusFilterChange(
                          status as "all" | "active" | "inactive",
                        );
                        setIsFilterOpen(false);
                        onPageChange(1); // Reset to first page on filter change
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 capitalize ${
                        statusFilter === status ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      {status === "all" ? "All Status" : status}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Primary button */}
          {canCreate && (
            <Button
              variant="primary"
              size="lg"
              className="w-full sm:w-auto h-[48px] sm:h-[44px]"
              onClick={onAddClick}
            >
              <Ticket size={16} className="mr-2" />
              Add Coupon
            </Button>
          )}
        </div>
      </div>

      <Card className="p-0 border shadow-sm overflow-visible">
        <div className="overflow-x-auto">
          <div className="overflow-x-auto">
            <table className="min-w-[1000px] w-full text-left text-sm border-separate border-spacing-0">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                    Code
                  </th>
                  <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                    Discount
                  </th>
                  <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                    Usage
                  </th>
                  <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                    Valid Until
                  </th>
                  <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                    Status
                  </th>
                  <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                    Revenue
                  </th>
                  <th className="px-6 py-4 w-[50px] bg-gray-50 border-b border-gray-100"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      Loading coupons...
                    </td>
                  </tr>
                ) : coupons.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      No coupons found
                    </td>
                  </tr>
                ) : (
                  coupons.map((coupon) => (
                    <tr
                      key={coupon._id}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold text-[#102040] bg-gray-100 px-2 py-1 rounded">
                            {coupon.code}
                          </span>
                          <button
                            onClick={() => copyToClipboard(coupon.code)}
                            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
                            title="Copy code"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                        {coupon.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                            {coupon.description}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium text-green-600 text-lg">
                          {getDiscountDisplay(coupon)}
                        </span>
                        <span className="text-xs text-gray-500 ml-1">
                          {coupon.discountType === "percentage"
                            ? "off"
                            : "fixed"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm">
                          {getUsageDisplay(coupon)}
                        </span>
                        {coupon.usageLimit && (
                          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                            <div
                              className="bg-blue-500 h-1.5 rounded-full"
                              style={{
                                width: `${Math.min(
                                  100,
                                  (coupon.usedCount / coupon.usageLimit) * 100,
                                )}%`,
                              }}
                            ></div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {formatDate(coupon.expiresAt)}
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(coupon)}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <span className="font-medium">
                            $
                            {coupon.analytics?.totalRevenue?.toFixed(2) ||
                              "0.00"}
                          </span>
                          <p className="text-xs text-gray-500">
                            Saved: $
                            {coupon.analytics?.totalDiscount?.toFixed(2) ||
                              "0.00"}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right relative">
                        <button
                          className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (activeMenu?.id === coupon._id) {
                              setActiveMenu(null);
                            } else {
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              setActiveMenu({
                                id: coupon._id,
                                position: {
                                  top: rect.bottom,
                                  right: window.innerWidth - rect.right,
                                },
                              });
                            }
                          }}
                        >
                          <MoreHorizontal size={20} />
                        </button>

                        <TableActionMenu
                          isOpen={activeMenu?.id === coupon._id}
                          onClose={() => setActiveMenu(null)}
                          position={activeMenu?.position || null}
                        >
                          <button
                            onClick={() => {
                              onViewClick(coupon);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                          >
                            <Eye size={14} className="text-blue-500" />
                            View Details
                          </button>
                          {canViewUsage && (
                            <button
                              onClick={() => {
                                onViewClick(coupon);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                            >
                              <BarChart3 size={14} className="text-purple-500" />
                              View Analytics
                            </button>
                          )}
                          <div className="h-px bg-gray-100 my-1" />
                          {canUpdate && (
                            <button
                              onClick={() => {
                                onEditClick(coupon);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                            >
                              <Edit2 size={14} className="text-blue-500" />
                              Edit Coupon
                            </button>
                          )}
                          {canToggle && onToggleStatus && (
                            <button
                              onClick={() => {
                                onToggleStatus(coupon);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                            >
                              {coupon.isActive ? (
                                <>
                                  <XCircle
                                    size={14}
                                    className="text-yellow-500"
                                  />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <CheckCircle2
                                    size={14}
                                    className="text-green-500"
                                  />
                                  Activate
                                </>
                              )}
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => {
                                onDeleteClick(coupon);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            >
                              <Trash2 size={14} />
                              Delete Coupon
                            </button>
                          )}
                        </TableActionMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-500">
            Showing {totalCoupons > 0 ? startItem : 0} to {endItem} of{" "}
            {totalCoupons} coupons
          </div>

          <div className="flex items-center gap-2">
            {/* Page size selector */}
            <select
              value={pageSize}
              onChange={(e) => {
                onPageSizeChange(Number(e.target.value));
                onPageChange(1); // Reset to first page
              }}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>

            {/* Pagination controls */}
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
              >
                ‹
              </Button>

              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "primary" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(pageNum)}
                    className="h-8 w-8 p-0"
                  >
                    {pageNum}
                  </Button>
                );
              })}

              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="h-8 w-8 p-0"
              >
                ›
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
